import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CheckCircle, ArrowRight, Zap, Award, Shield, Microscope, Facebook } from "lucide-react";

const Electropolishing = () => {
  const advantages = [
    {
      icon: Shield,
      title: "Superior Corrosion Resistance",
      description: "Removes surface contaminants and creates passive chromium-rich layer"
    },
    {
      icon: Microscope,
      title: "Ultra-Smooth Surface",
      description: "Eliminates microscopic peaks and valleys for easier cleaning"
    },
    {
      icon: Award,
      title: "Pharmaceutical Grade",
      description: "Meets USP Class VI and FDA requirements for biotech applications"
    },
    {
      icon: Zap,
      title: "Enhanced Cleanability",
      description: "Reduced surface roughness prevents bacterial adhesion and contamination"
    }
  ];

  const specifications = [
    { parameter: "Tank Dimensions", value: "1200mm (W) × 3000mm (L) × 1200mm (H)" },
    { parameter: "Maximum Part Size", value: "Limited only by tank dimensions" },
    { parameter: "Internal Tank EP", value: "Custom jigs available for any size" },
    { parameter: "Surface Testing", value: "MITUTOYO calibrated Ra testers" },
    { parameter: "Material Compatibility", value: "SS304, SS316L, Duplex, Titanium" },
    { parameter: "Industry Standards", value: "ASTM B912, ASME BPE" }
  ];

  const process = [
    {
      step: "01",
      title: "Pre-Cleaning",
      description: "Degreasing and mechanical cleaning preparation"
    },
    {
      step: "02",
      title: "Electropolishing Bath",
      description: "Controlled electrochemical surface removal process"
    },
    {
      step: "03",
      title: "Rinsing & Passivation",
      description: "Multiple rinse cycles and passivation treatment"
    },
    {
      step: "04",
      title: "Quality Testing",
      description: "Surface roughness and visual inspection verification"
    }
  ];

  const applications = [
    "Pharmaceutical processing equipment",
    "Biotech fermentation vessels",
    "Food grade storage tanks",
    "Chemical reactor vessels",
    "Medical device components",
    "Cosmetic processing equipment",
    "High-purity water systems",
    "Semiconductor fabrication equipment"
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Electropolishing Services | Southeast Asia's Largest EP Facility - WLE</title>
        <meta name="description" content="Southeast Asia's largest electropolishing facility. Pharmaceutical-grade surface finishing with Ra &lt;0.4μm. Tank dimensions up to 1200×3000×1200mm. MITUTOYO certified testing." />
        <link rel="canonical" href="https://wletec.com/services/electropolishing" />
        <meta property="og:title" content="Electropolishing Services - WLE Technology" />
        <meta property="og:description" content="Largest electropolishing facility in Southeast Asia for pharmaceutical-grade finishing." />
        <meta property="og:url" content="https://wletec.com/services/electropolishing" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="electropolishing, surface finishing, pharmaceutical grade, stainless steel polishing, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-4">Electropolishing</Badge>
            <h1 className="text-5xl font-bold text-foreground mb-6">
              <span className="text-wle-blue">Electropolishing</span> Services
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              One of Southeast Asia's largest electropolishing facilities delivering pharmaceutical-grade surface finishing 
              for critical applications requiring the highest levels of cleanliness and corrosion resistance.
            </p>
          </div>
        </div>
      </section>

      {/* Facility Highlight */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white mb-16">
            <CardContent className="p-12 text-center">
              <Zap className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">Largest EP Tank in Southeast Asia</h2>
              <p className="text-xl opacity-90 mb-6">
                Our facility houses the region's largest electropolishing tank, capable of handling 
                components up to 1200 × 3000 × 1200 mm
              </p>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold">1200mm</div>
                  <div className="opacity-90">Width</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">3000mm</div>
                  <div className="opacity-90">Length</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">1200mm</div>
                  <div className="opacity-90">Height</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Photo Gallery Section - Actual Work Showcase */}
      <section className="py-16 bg-subtle-texture">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Electropolishing Work</h2>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
              See the mirror-finish quality we achieve with our state-of-the-art electropolishing facility
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Electropolished Components Gallery */}
            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/24491102-4485-4bda-8c60-23c68765af73.png" 
                  alt="Electropolished tank lid with mirror finish showing precise component engineering"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Tank Lid Components</h3>
                <p className="text-sm text-foreground/70">Perfect mirror finish on complex tank lid assemblies</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/c72d2960-5358-465d-9d2a-e07637608175.png" 
                  alt="CF8M stainless steel electropolished component with material grade marking"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">CF8M Grade Components</h3>
                <p className="text-sm text-foreground/70">Electropolished CF8M stainless steel with visible grade markings</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/dfab705b-e10e-4e5d-baba-d8adcfdce6d8.png" 
                  alt="Electropolished stainless steel spring with perfect mirror finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Spring Components</h3>
                <p className="text-sm text-foreground/70">Precision electropolishing on complex curved geometries</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/aa3e6083-bca9-48b6-9842-e722e85b078c.png" 
                  alt="Electropolished valve component showing mirror interior surface finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Valve Components</h3>
                <p className="text-sm text-foreground/70">Mirror finish achieved on internal valve surfaces</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/8e512454-47db-42f6-b45c-99518e0b16c8.png" 
                  alt="Large electropolished tank lid with internal agitator components"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Tank Covers & Agitators</h3>
                <p className="text-sm text-foreground/70">Large-scale electropolishing for tank covers and internal components</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/d5ddeafb-a8b2-4021-92d1-825f9f1307e2.png" 
                  alt="Electropolished industrial fitting with threaded connections and mirror finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Industrial Fittings</h3>
                <p className="text-sm text-foreground/70">Complex threaded fittings with pharmaceutical-grade finish</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/43353152-18d0-4b61-af39-bf0441345d12.png" 
                  alt="Tank interior showing perfect mirror electropolish finish with Ra 0.1 micron surface"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Tank Interior Mirror Finish</h3>
                <p className="text-sm text-foreground/70">Ra 0.1 µm surface finish achieved on tank interiors</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/dfe2b115-06a4-409e-b9e6-7ad289c266e7.png" 
                  alt="Multiple electropolished stainless steel plates showing consistent mirror finish quality"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Plate Components</h3>
                <p className="text-sm text-foreground/70">Consistent mirror finish across multiple components</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/0de5e98c-1b46-441e-bcbc-e3621a06aad3.png" 
                  alt="Electropolished process fittings and reducers with flanged connections"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Process Fittings</h3>
                <p className="text-sm text-foreground/70">Flanged fittings and reducers with mirror electropolish finish</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/0661e2a6-27ec-44d1-9bf1-8f40e9699d44.png" 
                  alt="Large electropolished manifold distribution header with multiple connection ports"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Distribution Manifolds</h3>
                <p className="text-sm text-foreground/70">Complex manifold systems with multiple connection points</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/3a6522d8-a63b-475e-b181-49082f85cb1f.png" 
                  alt="Large electropolished industrial tank with mirror finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Industrial Tank Processing</h3>
                <p className="text-sm text-foreground/70">Large tank components with perfect mirror finish achieved in our facility</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/180d4c61-0e45-4211-8fa0-a327d8b25205.png" 
                  alt="Electropolished tank interior with multiple chambers"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Complex Tank Interiors</h3>
                <p className="text-sm text-foreground/70">Multi-chamber tank systems with uniform electropolished finish</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/a3417f86-66be-4141-81a3-5d8b3e3c25fa.png" 
                  alt="Electropolished bowl components with mirror finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Bowl Components</h3>
                <p className="text-sm text-foreground/70">Precision bowl components with exceptional mirror finish quality</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/24ec047e-d14d-4066-b194-ab777df93782.png" 
                  alt="Electropolished rectangular tank with perfect interior finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Rectangular Tanks</h3>
                <p className="text-sm text-foreground/70">Custom tank configurations with flawless electropolished surfaces</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/2a006442-e6e6-4ab5-8317-4a3252776817.png" 
                  alt="Large electropolished pump casings with mirror finish"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Industrial Pump Casings</h3>
                <p className="text-sm text-foreground/70">Heavy-duty pump casings with superior mirror finish quality</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/8be409aa-9ed0-4aef-9512-a4df5ab7be53.png" 
                  alt="Electropolished pump interior with reflective surface"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Pump Interior Finish</h3>
                <p className="text-sm text-foreground/70">Internal pump components with uniform electropolished surface</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/c26dc220-f9cd-4b5c-9b76-f2b1c10e36d6.png" 
                  alt="Tank interior showing mirror-like reflection quality"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Mirror-Like Reflections</h3>
                <p className="text-sm text-foreground/70">Demonstrating the reflective quality of our electropolishing process</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/9952621f-874a-4159-9d79-f74cb71af0af.png" 
                  alt="Long electropolished vessel components"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Extended Vessel Components</h3>
                <p className="text-sm text-foreground/70">Long-form vessel components with consistent finish quality</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/ac18b1ae-dbb0-4a03-a4eb-0ed7849a814c.png" 
                  alt="Multiple pump casings showing uniform electropolish quality"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Batch Processing Quality</h3>
                <p className="text-sm text-foreground/70">Multiple components showing consistent electropolishing results</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video relative">
                <img 
                  src="/lovable-uploads/783fe3af-b26d-42fd-94fe-f92e7a762273.png" 
                  alt="Tank interior with polished finish reflecting workshop equipment"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2">Workshop Reflections</h3>
                <p className="text-sm text-foreground/70">Tank interior finish quality reflecting our precision manufacturing environment</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block p-6 bg-card/50 backdrop-blur-sm">
              <div className="flex items-center justify-center space-x-4">
                <Award className="h-8 w-8 text-wle-blue" />
                <div className="text-left">
                  <h3 className="font-semibold">Mirror Finish Ra 0.1 µm</h3>
                  <p className="text-sm text-foreground/70">MITUTOYO certified surface roughness testing</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Electropolishing Advantages</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((advantage, index) => (
              <Card key={index} className="text-center hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 rounded-full bg-wle-blue/10">
                    <advantage.icon className="h-6 w-6 text-wle-blue" />
                  </div>
                  <CardTitle className="text-lg">{advantage.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{advantage.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Technical Specifications</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Facility Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between border-b pb-2">
                      <span className="font-medium">{spec.parameter}</span>
                      <span className="text-muted-foreground text-right">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Quality Assurance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>MITUTOYO calibrated surface roughness testing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>ASTM B912 standard compliance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Visual inspection and documentation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Process parameter recording</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Material traceability certificates</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">EP Process Flow</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2 text-lg">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Applications & Industries</h2>
              <p className="text-muted-foreground mb-8">
                Electropolishing is essential for applications requiring the highest levels of surface cleanliness, 
                corrosion resistance, and biocompatibility.
              </p>
              <ul className="space-y-4">
                {applications.map((application, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-wle-blue mt-0.5 flex-shrink-0" />
                    <span>{application}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="p-8">
                <div className="text-center">
                  <Award className="h-16 w-16 text-wle-blue mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Custom Solutions</h3>
                  <p className="text-muted-foreground mb-6">
                    No size limitations for internal tank electropolishing. We create custom jigs and 
                    portable EP pools for tanks of any dimension.
                  </p>
                  <ul className="space-y-2 text-sm text-left">
                    <li>• Portable EP equipment available</li>
                    <li>• Custom jig design and fabrication</li>
                    <li>• On-site EP services for large tanks</li>
                    <li>• Specialized solutions for unique geometries</li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Need Pharmaceutical-Grade Finishing?</h3>
              <p className="text-lg mb-8 opacity-90">
                Contact us for electropolishing services that meet the most stringent industry requirements. 
                From small components to large tank internals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/contact">
                    Get EP Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                  <a 
                    href="https://www.facebook.com/profile.php?id=100054675420087" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <Facebook className="h-4 w-4" />
                    WLE Electro Polish
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Electropolishing;